import React, { useMemo, useState, useRef, Children } from 'react';
import { CaretDown, Check, MagnifyingGlass, X, CaretRight } from '@phosphor-icons/react';
import { LOCATION_TREE } from '../../../lib/constants';
import { cn } from '../../../lib/utils';
import { FilterPanel } from './FilterPanel';
interface LocationDropdownProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  resultCount: number;
}
export function LocationDropdown({
  selected,
  onChange,
  resultCount
}: LocationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['București']));
  const ref = useRef<HTMLDivElement>(null);
  const toggleExpand = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);else next.add(value);
      return next;
    });
  };
  // Helper to get all leaf values under a node
  const getLeaves = (node: any): string[] => {
    if (!node.children) return [node.value];
    return node.children.flatMap((c: any) => getLeaves(c));
  };
  const toggleNode = (node: any) => {
    const leaves = getLeaves(node);
    const allSelected = leaves.every(l => selected.includes(l));
    let newSelected = [...selected];
    if (allSelected) {
      // Deselect all
      newSelected = newSelected.filter(s => !leaves.includes(s));
    } else {
      // Select all
      leaves.forEach(l => {
        if (!newSelected.includes(l)) newSelected.push(l);
      });
    }
    onChange(newSelected);
  };
  const removeSelected = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    onChange(selected.filter(s => s !== value));
  };
  // Flatten tree for search
  const flatNodes = useMemo(() => {
    const flat: any[] = [];
    const traverse = (nodes: any[]) => {
      nodes.forEach(n => {
        flat.push(n);
        if (n.children) traverse(n.children);
      });
    };
    traverse(LOCATION_TREE);
    return flat;
  }, []);
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return flatNodes.filter(n => n.label.toLowerCase().includes(q));
  }, [search, flatNodes]);
  // Render a node and its children recursively
  const renderNode = (node: any, depth = 0) => {
    const leaves = getLeaves(node);
    const selectedLeavesCount = leaves.filter(l => selected.includes(l)).length;
    const allSelected = selectedLeavesCount === leaves.length;
    const indeterminate = selectedLeavesCount > 0 && !allSelected;
    const isExpanded = expandedNodes.has(node.value);
    const hasChildren = !!node.children;
    return <div key={node.value} data-id="element-2393">
        <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-dark-3 rounded-lg cursor-pointer group" style={{
        paddingLeft: `${depth * 16 + 12}px`
      }} onClick={() => toggleNode(node)} data-id="element-2394">
          {hasChildren ? <button type="button" onClick={e => toggleExpand(e, node.value)} className="w-5 h-5 flex items-center justify-center text-light-4 hover:text-light-1 shrink-0" data-id="element-2395">
              <CaretRight size={14} className={cn('transition-transform', isExpanded && 'rotate-90')} data-id="element-2396" />
            </button> : <div className="w-5 shrink-0" data-id="element-2397" />}

          <div className={cn('w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors', allSelected ? 'bg-lime-1 border-lime-1 text-dark-1' : indeterminate ? 'bg-lime-1/20 border-lime-1 text-lime-1' : 'border-light-3')} data-id="element-2398">
            {allSelected && <Check size={12} weight="bold" data-id="element-2399" />}
            {indeterminate && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-2400" />}
          </div>

          <span className={cn('text-sm truncate', depth === 0 ? 'text-light-1 font-medium' : 'text-light-2')} data-id="element-2401">
            {node.label}
          </span>
        </div>

        {hasChildren && isExpanded && <div className="mt-0.5" data-id="element-2402">
            {node.children.map((child: any) => renderNode(child, depth + 1))}
          </div>}
      </div>;
  };
  // Collapse selected leaves up to the highest fully-selected ancestor.
  // Walks LOCATION_TREE from the roots. For each node: if every leaf under
  // it is selected → return that node as a single display item and stop
  // recursing; otherwise descend into its children. Pure leaves with no
  // ancestor-collapse are returned as-is.
  const collapsedSelection = useMemo(() => {
    const selectedSet = new Set(selected);
    const result: {
      value: string;
      label: string;
      leaves: string[];
    }[] = [];
    const walk = (node: any) => {
      const leaves = getLeaves(node);
      if (leaves.length === 0) return;
      const allSelected = leaves.every(l => selectedSet.has(l));
      if (allSelected) {
        result.push({
          value: node.value,
          label: node.label,
          leaves
        });
        return;
      }
      if (node.children) {
        node.children.forEach((child: any) => walk(child));
      }
    };
    LOCATION_TREE.forEach(root => walk(root));
    return result;
  }, [selected]);
  // Display label logic — uses collapsed selection so a fully-selected city
  // reads as "București" (1) instead of "24 locații selectate".
  const getDisplayLabel = () => {
    if (selected.length === 0) return 'Toate orașele';
    if (collapsedSelection.length === 1) return collapsedSelection[0].label;
    return `${collapsedSelection.length} locații selectate`;
  };
  // Remove an entire collapsed node (clears all its leaves at once).
  const removeCollapsed = (e: React.MouseEvent, leaves: string[]) => {
    e.stopPropagation();
    const leafSet = new Set(leaves);
    onChange(selected.filter(s => !leafSet.has(s)));
  };
  const handleClear = () => onChange([]);
  return <div className="relative min-w-0 sm:flex-1" ref={ref} data-id="element-2403">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={cn('w-full h-10 px-4 rounded-xl border text-sm flex items-center justify-between gap-2 transition-colors cursor-pointer', selected.length > 0 ? 'bg-dark-2 border-lime-1 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-light-4')} data-id="element-2404">
        <span className="truncate min-w-0" data-id="element-2405">{getDisplayLabel()}</span>
        <CaretDown size={14} className={cn('shrink-0 transition-transform', isOpen && 'rotate-180')} data-id="element-2406" />
      </button>

      <FilterPanel isOpen={isOpen} onClose={() => setIsOpen(false)} title="Locații" anchorRef={ref} desktopWidthClass="sm:w-80 sm:max-h-[400px]" resultCount={resultCount} secondaryAction={selected.length > 0 ? {
      label: 'Resetează',
      onClick: handleClear
    } : null} data-id="element-2407">
        {/* Search Input */}
        <div className="p-3 border-b border-white/10 shrink-0" data-id="element-2408">
          <div className="relative" data-id="element-2409">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-4" data-id="element-2410" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Caută oraș, sector, zonă..." className="w-full h-9 bg-dark-3 border border-dark-4 rounded-lg pl-9 pr-3 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 focus:ring-1 focus:ring-lime-1" data-id="element-2411" />
          </div>
        </div>

        {/* Selected Chips */}
        {collapsedSelection.length > 0 && !search && <div className="px-3 py-2 border-b border-white/10 flex flex-wrap gap-1.5 shrink-0 max-h-24 overflow-y-auto dark-scrollbar" data-id="element-2412">
            {collapsedSelection.slice(0, 10).map(n => <span key={n.value} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-dark-3 border border-dark-4 text-xs text-light-2" data-id="element-2413">
                {n.label}
                <button onClick={e => removeCollapsed(e, n.leaves)} className="hover:text-light-1" data-id="element-2414">
                  <X size={10} weight="bold" data-id="element-2415" />
                </button>
              </span>)}
            {collapsedSelection.length > 10 && <span className="inline-flex items-center px-2 py-1 rounded bg-dark-3 border border-dark-4 text-xs text-light-4" data-id="element-2416">
                +{collapsedSelection.length - 10} altele
              </span>}
          </div>}

        {/* Tree or Search Results */}
        <div className="p-2" data-id="element-2417">
          {searchResults ? searchResults.length > 0 ? <div className="space-y-1" data-id="element-2418">
                {searchResults.map(node => {
            const isLeaf = !node.children;
            const isSelected = isLeaf ? selected.includes(node.value) : false; // Simplified for search
            return <button key={node.value} type="button" onClick={() => toggleNode(node)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-dark-3 transition-colors text-left" data-id="element-2419">
                      <div className={cn('w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors', isSelected ? 'bg-lime-1 border-lime-1 text-dark-1' : 'border-light-3')} data-id="element-2420">
                        {isSelected && <Check size={12} weight="bold" data-id="element-2421" />}
                      </div>
                      <div data-id="element-2422">
                        <div className="text-sm text-light-1 font-medium" data-id="element-2423">
                          {node.label}
                        </div>
                        <div className="text-xs text-light-4 truncate" data-id="element-2424">
                          {node.value.replace(/\|/g, ' > ')}
                        </div>
                      </div>
                    </button>;
          })}
              </div> : <div className="py-8 text-center text-sm text-light-4" data-id="element-2425">
                Nu am găsit rezultate pentru "{search}"
              </div> : <div className="space-y-1" data-id="element-2426">
              {LOCATION_TREE.map(node => renderNode(node))}
            </div>}
        </div>
      </FilterPanel>
    </div>;
}